import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { FaEye } from 'react-icons/fa';
import axios from '../Config/axiosConfig';
import CustomPagination from '../Shared/CustomJs/CustomPagination';
import '../Shared/sharedStyles.css';
import './Logs.css';

const baseApi = `${process.env.REACT_APP_API_URL}/api/Logs`;
const filtrosIniciais = {
  pesquisa: '', dataInicio: '', dataFim: '', nivel: '', tipo: '',
  modulo: '', entidade: '', acao: '', sucesso: ''
};
const quantidadePorPagina = 50;
const resumoInicial = {
  totalEventos: 0, eventosHoje: 0, loginsHoje: 0, logoutsHoje: 0,
  usuariosHoje: 0, usuariosPeriodo: 0, logins: 0, falhasLogin: 0, logouts: 0, outros: 0
};

const formatarData = (valor) => new Date(valor).toLocaleString('pt-BR', {
  dateStyle: 'short', timeStyle: 'medium'
});

const formatarDia = (valor) => {
  const [, mes, dia] = valor.split('-');
  return `${dia}/${mes}`;
};

const tipoEvento = (log = {}) => {
  const acao = (log.acao || '').toLocaleUpperCase('pt-BR');
  const descricao = (log.descricao || '').toLocaleLowerCase('pt-BR');
  if (acao === 'LOGIN' && log.sucesso === false) return { texto: 'Falha', cor: 'danger' };
  if (acao === 'LOGIN' || descricao.includes('logado com sucesso')) return { texto: 'Sucesso', cor: 'success' };
  if (acao === 'LOGOUT' || descricao.includes('logout')) return { texto: 'Sucesso', cor: 'success' };
  if (log.sucesso === false) return { texto: 'Falha', cor: 'danger' };
  if (log.sucesso === true) return { texto: 'Sucesso', cor: 'success' };
  return { texto: 'Não informado', cor: 'secondary' };
};

const corNivel = (nivel = '') => ({
  ERRO: 'danger', AVISO: 'warning', WARNING: 'warning', INFO: 'secondary', CRITICO: 'dark', 'CRÍTICO': 'dark'
}[nivel.toLocaleUpperCase('pt-BR')] || 'secondary');

const valorOuTraco = (valor) => valor === null || valor === undefined || valor === '' ? '—' : valor;

const mensagemErro = (error) => error?.response?.data?.errors?.[0]
  || error?.response?.data?.mensagem
  || 'Não foi possível carregar os logs.';

const opcoesGrafico = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
};

const CardIndicador = ({ titulo, valor, cor }) => (
  <div className='logs-indicador'>
    <span>{titulo}</span>
    <strong className={`text-${cor}`}>{valor}</strong>
  </div>
);

const ListaRanking = ({ titulo, items }) => (
  <div className='logs-ranking'>
    <strong>{titulo}</strong>
    {items.length === 0 ? <span className='text-muted mt-3'>Sem dados no período.</span>
      : items.map((item) => <div className='logs-ranking-item' key={`${titulo}-${item.label}`}>
        <span>{item.label || 'Não informado'}</span><b>{item.quantidade}</b>
      </div>)}
  </div>
);

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState(filtrosIniciais);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciais);
  const [resumo, setResumo] = useState(resumoInicial);
  const [eventosPorDia, setEventosPorDia] = useState([]);
  const [eventosPorHora, setEventosPorHora] = useState([]);
  const [topUsuarios, setTopUsuarios] = useState([]);
  const [tiposEvento, setTiposEvento] = useState([]);
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async (novaPagina, novosFiltros) => {
    setLoading(true);
    setErro('');
    try {
      const response = await axios.get(baseApi, {
        params: {
          pagina: novaPagina, quantidade: quantidadePorPagina,
          pesquisa: novosFiltros.pesquisa.trim(),
          dataInicio: novosFiltros.dataInicio || undefined,
          dataFim: novosFiltros.dataFim || undefined,
          nivel: novosFiltros.nivel || undefined,
          tipo: novosFiltros.tipo.trim() || undefined,
          modulo: novosFiltros.modulo.trim() || undefined,
          entidade: novosFiltros.entidade.trim() || undefined,
          acao: novosFiltros.acao.trim() || undefined,
          sucesso: novosFiltros.sucesso === '' ? undefined : novosFiltros.sucesso === 'true'
        }
      });
      setLogs(response.data.items || []);
      setTotal(response.data.total || 0);
      setPagina(response.data.pagina || novaPagina);
      setResumo(response.data.resumo || resumoInicial);
      setEventosPorDia(response.data.eventosPorDia || []);
      setEventosPorHora(response.data.eventosPorHora || []);
      setTopUsuarios(response.data.topUsuarios || []);
      setTiposEvento(response.data.tiposEvento || []);
    } catch (error) {
      setLogs([]); setTotal(0); setResumo(resumoInicial);
      setEventosPorDia([]); setEventosPorHora([]); setTopUsuarios([]); setTiposEvento([]);
      setErro(mensagemErro(error));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(1, filtrosIniciais); }, [carregar]);

  const alterarFiltro = (campo, valor) => setFiltros((atuais) => ({ ...atuais, [campo]: valor }));

  const pesquisar = (event) => {
    event.preventDefault();
    if (filtros.dataInicio && filtros.dataFim && filtros.dataInicio > filtros.dataFim) {
      setErro('A data inicial não pode ser maior que a data final.'); return;
    }
    const novosFiltros = { ...filtros, pesquisa: filtros.pesquisa.trim() };
    setFiltrosAplicados(novosFiltros);
    carregar(1, novosFiltros);
  };

  const limpar = () => {
    setFiltros(filtrosIniciais); setFiltrosAplicados(filtrosIniciais);
    carregar(1, filtrosIniciais);
  };

  const totalPaginas = Math.ceil(total / quantidadePorPagina);
  const percentualLogin = resumo.totalEventos
    ? `${((resumo.logins / resumo.totalEventos) * 100).toFixed(1).replace('.', ',')}%` : '0%';

  const dadosDia = useMemo(() => ({
    labels: eventosPorDia.map((item) => formatarDia(item.label)),
    datasets: [{ label: 'Eventos', data: eventosPorDia.map((item) => item.quantidade),
      borderColor: '#0d6efd', backgroundColor: 'rgba(13,110,253,.16)', fill: true, tension: .25 }]
  }), [eventosPorDia]);

  const dadosUsuarios = useMemo(() => ({
    labels: topUsuarios.map((item) => item.label),
    datasets: [{ label: 'Eventos', data: topUsuarios.map((item) => item.quantidade),
      backgroundColor: '#0d6efd', borderRadius: 7 }]
  }), [topUsuarios]);

  const dadosTipos = useMemo(() => ({
    labels: tiposEvento.map((item) => item.label),
    datasets: [{ data: tiposEvento.map((item) => item.quantidade),
      backgroundColor: tiposEvento.map((item) => item.label === 'Login' ? '#198754'
        : item.label === 'Falha de login' ? '#dc3545' : item.label === 'Logout' ? '#6c757d' : '#0d6efd') }]
  }), [tiposEvento]);

  const dadosHora = useMemo(() => ({
    labels: eventosPorHora.map((item) => item.label),
    datasets: [{ label: 'Eventos', data: eventosPorHora.map((item) => item.quantidade),
      borderColor: '#f59f00', backgroundColor: 'rgba(245,159,0,.16)', fill: true, tension: .25 }]
  }), [eventosPorHora]);

  return (
    <Container fluid className='py-3 logs-page'>
      <div className='mb-3'><h3 className='mb-1'>Logs</h3><p className='text-muted mb-0'>Auditoria de acessos e eventos registrados pelo sistema.</p></div>

      <div className='logs-indicadores mb-3'>
        <CardIndicador titulo='Eventos hoje' valor={resumo.eventosHoje} cor='primary' />
        <CardIndicador titulo='Logins hoje' valor={resumo.loginsHoje} cor='success' />
        <CardIndicador titulo='Falhas de login' valor={resumo.falhasLogin} cor='danger' />
        <CardIndicador titulo='Logouts hoje' valor={resumo.logoutsHoje} cor='secondary' />
        <CardIndicador titulo='Usuários hoje' valor={resumo.usuariosHoje} cor='warning' />
        <CardIndicador titulo='Total de eventos' valor={resumo.totalEventos} cor='dark' />
      </div>

      <Card className='mb-3'>
        <Card.Header className='fw-bold'>Painel gerencial</Card.Header>
        <Card.Body>
          <p className='text-muted'>Indicadores calculados conforme os filtros aplicados.</p>
          <div className='logs-indicadores logs-indicadores-painel mb-3'>
            <CardIndicador titulo='Usuários no período' valor={resumo.usuariosPeriodo} cor='primary' />
            <CardIndicador titulo='Logins' valor={resumo.logins} cor='success' />
            <CardIndicador titulo='Falhas' valor={resumo.falhasLogin} cor='danger' />
            <CardIndicador titulo='Logouts' valor={resumo.logouts} cor='secondary' />
            <CardIndicador titulo='Outros eventos' valor={resumo.outros} cor='info' />
            <CardIndicador titulo='Participação de login' valor={percentualLogin} cor='success' />
          </div>
          <Row className='g-3'>
            <Col lg={6}><ListaRanking titulo='Top usuários por atividade' items={topUsuarios} /></Col>
            <Col lg={6}><ListaRanking titulo='Tipos de evento' items={tiposEvento} /></Col>
          </Row>
        </Card.Body>
      </Card>

      <div className='logs-graficos mb-3'>
        <Card className='logs-grafico logs-grafico-dias'><Card.Body><strong>Eventos por dia</strong><div className='logs-canvas'><Line data={dadosDia} options={opcoesGrafico} /></div></Card.Body></Card>
        <Card className='logs-grafico'><Card.Body><strong>Usuários mais ativos</strong><div className='logs-canvas'><Bar data={dadosUsuarios} options={opcoesGrafico} /></div></Card.Body></Card>
        <Card className='logs-grafico'><Card.Body><strong>Tipos de evento</strong><div className='logs-canvas'><Doughnut data={dadosTipos} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} /></div></Card.Body></Card>
        <Card className='logs-grafico'><Card.Body><strong>Atividade por hora</strong><div className='logs-canvas'><Line data={dadosHora} options={opcoesGrafico} /></div></Card.Body></Card>
      </div>

      <Card className='mb-3'><Card.Header className='fw-bold'>Filtros</Card.Header><Card.Body>
        <Form onSubmit={pesquisar}>
          <Row className='g-2 align-items-end mb-2'>
            <Col xs={12} lg={6}><Form.Group controlId='log-pesquisa'><Form.Label>Busca livre</Form.Label>
              <Form.Control value={filtros.pesquisa} maxLength={100} placeholder='Descrição, detalhes, registro, IP ou URL…'
                onChange={(event) => alterarFiltro('pesquisa', event.target.value)} /></Form.Group></Col>
            <Col xs={12} sm={6} lg={3}><Form.Group controlId='log-data-inicio'><Form.Label>Data inicial</Form.Label>
              <Form.Control type='date' value={filtros.dataInicio} max={filtros.dataFim || undefined}
                onChange={(event) => alterarFiltro('dataInicio', event.target.value)} /></Form.Group></Col>
            <Col xs={12} sm={6} lg={3}><Form.Group controlId='log-data-fim'><Form.Label>Data final</Form.Label>
              <Form.Control type='date' value={filtros.dataFim} min={filtros.dataInicio || undefined}
                onChange={(event) => alterarFiltro('dataFim', event.target.value)} /></Form.Group></Col>
          </Row>
          <Row className='g-2 align-items-end'>
            <Col xs={12} sm={6} lg={2}><Form.Group controlId='log-nivel'><Form.Label>Nível</Form.Label>
              <Form.Select value={filtros.nivel} onChange={(event) => alterarFiltro('nivel', event.target.value)}>
                <option value=''>Todos</option><option value='INFO'>Info</option><option value='AVISO'>Aviso</option><option value='ERRO'>Erro</option><option value='CRÍTICO'>Crítico</option>
              </Form.Select></Form.Group></Col>
            <Col xs={12} sm={6} lg={2}><Form.Group controlId='log-tipo'><Form.Label>Tipo</Form.Label>
              <Form.Control value={filtros.tipo} maxLength={40} placeholder='Ex.: SEGURANÇA' onChange={(event) => alterarFiltro('tipo', event.target.value)} /></Form.Group></Col>
            <Col xs={12} sm={6} lg={2}><Form.Group controlId='log-modulo'><Form.Label>Módulo</Form.Label>
              <Form.Control value={filtros.modulo} maxLength={60} placeholder='Ex.: AUTENTICAÇÃO' onChange={(event) => alterarFiltro('modulo', event.target.value)} /></Form.Group></Col>
            <Col xs={12} sm={6} lg={2}><Form.Group controlId='log-entidade'><Form.Label>Entidade</Form.Label>
              <Form.Control value={filtros.entidade} maxLength={80} placeholder='Ex.: USUÁRIOS' onChange={(event) => alterarFiltro('entidade', event.target.value)} /></Form.Group></Col>
            <Col xs={12} sm={6} lg={2}><Form.Group controlId='log-acao'><Form.Label>Ação</Form.Label>
              <Form.Control value={filtros.acao} maxLength={60} placeholder='Ex.: LOGIN' onChange={(event) => alterarFiltro('acao', event.target.value)} /></Form.Group></Col>
            <Col xs={12} sm={6} lg={2}><Form.Group controlId='log-sucesso'><Form.Label>Resultado</Form.Label>
              <Form.Select value={filtros.sucesso} onChange={(event) => alterarFiltro('sucesso', event.target.value)}>
                <option value=''>Todos</option><option value='true'>Sucesso</option><option value='false'>Falha</option>
              </Form.Select></Form.Group></Col>
            <Col xs={12}><div className='d-flex gap-2 mt-2'>
              <Button type='submit' disabled={loading}>Pesquisar</Button>
              <Button type='button' variant='outline-secondary' onClick={limpar} disabled={loading}>Limpar</Button>
            </div></Col>
          </Row>
        </Form>
      </Card.Body></Card>

      <Card><Card.Header className='fw-bold'>Eventos</Card.Header><Card.Body className='p-0'>
        {erro && <Alert variant='danger' role='alert' className='m-3'>{erro}</Alert>}
        {loading ? <div className='text-center py-5' role='status'><Spinner animation='border' size='sm' className='me-2' />Carregando logs…</div>
          : logs.length === 0 ? <div className='text-center text-muted py-5'>Nenhum log encontrado.</div> : <>
            <Table responsive hover className='align-middle logs-table mb-0'><thead><tr><th></th><th>Data/Hora</th><th>Nível</th><th>Resultado</th><th>Módulo</th><th>Entidade</th><th>Ação</th><th>Usuário</th><th>Registro</th><th>Descrição</th></tr></thead>
              <tbody>{logs.map((log) => { const evento = tipoEvento(log); return <tr key={log.idLogs}>
                <td><Button variant='outline-secondary' size='sm' aria-label={`Detalhar log ${log.idLogs}`} onClick={() => setDetalhe(log)}><FaEye /></Button></td>
                <td className='logs-data'>{formatarData(log.data)}</td><td><Badge bg={corNivel(log.nivel)}>{valorOuTraco(log.nivel)}</Badge></td>
                <td><Badge bg={evento.cor}>{evento.texto}</Badge></td><td>{valorOuTraco(log.modulo)}</td><td>{valorOuTraco(log.entidade)}</td>
                <td>{valorOuTraco(log.acao)}</td><td>{valorOuTraco(log.user)}</td><td>{valorOuTraco(log.registro)}</td><td>{valorOuTraco(log.descricao)}</td></tr>; })}</tbody></Table>
            <div className='p-3 text-muted small'>Exibindo {logs.length} de {total} registros.</div>
            {totalPaginas > 1 && <CustomPagination currentPage={pagina} totalPages={totalPaginas} onPageChange={(novaPagina) => carregar(novaPagina, filtrosAplicados)} />}
          </>}
      </Card.Body></Card>

      <Modal show={Boolean(detalhe)} onHide={() => setDetalhe(null)} size='lg' centered>
        <Modal.Header closeButton><Modal.Title>Detalhe do Log <small className='d-block text-muted fs-6'>Log #{detalhe?.idLogs}</small></Modal.Title></Modal.Header>
        <Modal.Body>{detalhe && <Row className='g-3'>
          <Col md={6}><b>Data/Hora:</b> {formatarData(detalhe.data)}</Col>
          <Col md={6}><b>Nível:</b> {valorOuTraco(detalhe.nivel)}</Col>
          <Col md={6}><b>Tipo:</b> {valorOuTraco(detalhe.tipo)}</Col>
          <Col md={6}><b>Resultado:</b> {tipoEvento(detalhe).texto}</Col>
          <Col md={6}><b>Módulo:</b> {valorOuTraco(detalhe.modulo)}</Col>
          <Col md={6}><b>Entidade:</b> {valorOuTraco(detalhe.entidade)}</Col>
          <Col md={6}><b>Ação:</b> {valorOuTraco(detalhe.acao)}</Col>
          <Col md={6}><b>Usuário:</b> {valorOuTraco(detalhe.user)}</Col>
          <Col md={6}><b>Registro:</b> {valorOuTraco(detalhe.registro)}</Col>
          <Col md={6}><b>IP:</b> {valorOuTraco(detalhe.ip)}</Col>
          <Col md={6}><b>Método:</b> {valorOuTraco(detalhe.metodo)}</Col>
          <Col xs={12}><b>URL:</b> {valorOuTraco(detalhe.url)}</Col>
          <Col xs={12}><b>Descrição:</b><div className='logs-detalhe-descricao mt-2'>{valorOuTraco(detalhe.descricao)}</div></Col>
          <Col xs={12}><b>Detalhes:</b><div className='logs-detalhe-descricao mt-2'>{valorOuTraco(detalhe.detalhes)}</div></Col>
          <Col md={6}><b>Payload antes:</b><pre className='logs-detalhe-descricao logs-payload mt-2'>{valorOuTraco(detalhe.payloadAntes)}</pre></Col>
          <Col md={6}><b>Payload depois:</b><pre className='logs-detalhe-descricao logs-payload mt-2'>{valorOuTraco(detalhe.payloadDepois)}</pre></Col>
        </Row>}</Modal.Body>
        <Modal.Footer><Button variant='secondary' onClick={() => setDetalhe(null)}>Fechar</Button></Modal.Footer>
      </Modal>
    </Container>
  );
}
